import React, { type JSX } from 'react';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {Tooltip} from "@mui/material";
/**
 * @interface CustomButtonProps
 * @description Define las propiedades para el componente CustomButton.
 * @property {"submit" | "reset" | "button" | undefined} type - El tipo de botón HTML.
 * @property {string} text - El texto a mostrar dentro del botón.
 * @property {string} [className] - Clases CSS adicionales para aplicar al botón.
 * @property {boolean} [disabled] - Si es verdadero, el botón estará deshabilitado.
 * @property {boolean} [isLoading] - Si es verdadero, muestra un estado de carga y deshabilita el botón.
 * @property {() => void} [onClick] - Función de clic opcional para el botón.
 * @property {string} [ariaLabel] - Nombre accesible del botón. Hace falta en los botones de solo
 * ícono: deshabilitado con `tooltip`, MUI nombra al `span` envolvente y no al botón.
 */
interface CustomButtonProps {
  type: 'submit' | 'reset' | 'button' | undefined;
  text?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  tooltip?: string;
  icon?: IconDefinition;
  onClick?: () => void;
  ariaLabel?: string;
}

/**
 * Un componente de botón personalizable y accesible con un estilo consistente.
 * Incluye estados de foco visible, deshabilitado y de carga.
 *
 * Comportamiento del estado deshabilitado (`disabled` o `isLoading`):
 * - Aplica `opacity-50 cursor-not-allowed` y retira `cursor-pointer` para que el
 *   botón se vea inhabilitado y el cursor no compita entre dos utilidades del mismo
 *   grupo.
 * - Cuando además hay `tooltip`, el botón se envuelve en un `span` que pasa a ser
 *   el hijo del Tooltip y el botón recibe `pointer-events-none`. Ambas piezas son
 *   obligatorias: el navegador no despacha eventos de puntero desde un `button`
 *   deshabilitado ni los propaga a su padre, así que sin `pointer-events-none` el
 *   hit-testing nunca llega al `span` y sin el `span` no existe ningún elemento
 *   habilitado que escuche el hover. Como `pointer-events-none` anula el cursor del
 *   botón, el `span` lleva `cursor-not-allowed`; además usa `inline-flex` para no
 *   alterar el layout (MUI necesita un elemento de bloque o flex bajo el Tooltip
 *   para posicionar correctamente en Safari).
 * - Sin `tooltip` no se aplica `pointer-events-none`, para que el propio botón
 *   muestre su `cursor-not-allowed`.
 *
 * Cuando el botón está habilitado, o cuando no hay `tooltip`, el árbol renderizado
 * es idéntico al original para no afectar a los demás consumidores.
 *
 * @component
 * @param {CustomButtonProps} props - Las propiedades para el componente.
 * @returns {JSX.Element} Un elemento de botón estilizado y accesible.
 */
export const CustomButton: React.FC<CustomButtonProps> = ({
  tooltip,
  type,
  text,
  className,
  onClick,
  disabled,
  isLoading,
  icon,
  ariaLabel,
}: CustomButtonProps): JSX.Element => {
  const isDisabled = disabled || isLoading;
  // Solo el botón deshabilitado con tooltip necesita el envoltorio y liberar sus
  // eventos de puntero; en cualquier otro caso el botón conserva su propio cursor.
  const envolverEnSpan = isDisabled && !!tooltip;

  // Las clases del consumidor van al final para que sus utilidades `disabled:*`
  // sigan mandando sobre las clases base.
  const clasesDeEstado = isDisabled
    ? envolverEnSpan
      ? 'opacity-50 pointer-events-none'
      : 'opacity-50 cursor-not-allowed'
    : 'cursor-pointer';
  const clasesDelBoton = `rounded-xl ${clasesDeEstado} transition-all duration-200 ${className ? className : ""}`;

  const boton = (
    <button
      type={type}
      className={clasesDelBoton}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={isLoading ? 'true' : 'false'}
      // Solo si viene: una llave `aria-label` aunque sea `undefined` pisa la que inyecta el Tooltip
      // de MUI y deja sin nombre a los botones de solo ícono que no pasan `ariaLabel`.
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {icon && <FontAwesomeIcon icon={icon} size="sm" />}
      {isLoading ? 'Cargando...' : text}
    </button>
  );

  // Solo se envuelve en el caso que lo necesita: un botón deshabilitado con tooltip.
  const hijoDelTooltip = envolverEnSpan ? (
    <span className="inline-flex cursor-not-allowed">{boton}</span>
  ) : (
    boton
  );

  return (
    <Tooltip
      title={tooltip || ''}
      placement="top"
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
      }}
    >
      {hijoDelTooltip}
    </Tooltip>
  );
};

export default CustomButton;
